import { type Logger } from '@wix/fe-essentials/bi';
import type { i18n } from '@wix/fe-essentials/i18n';
import type { IHttpClient } from '@wix/fe-essentials/http-client';
import {
  BiContext,
  ConfigContext,
  type Currency,
  type Experiments,
  ExperimentsContext,
  type ExternalData,
  ExternalDataProvider,
  type ForcedState,
  ForcedStateContext,
  FormContext,
  HttpClientContext,
  TranslationContext,
  WixRicosViewerContext,
  AddressFormsContext,
  getAddressForm,
  type AddressTemplate,
  EventsProvider,
  type UserData,
  FieldInitialDataContext,
  type FieldInitialData,
  PanoramaContext,
  FocusedFieldProvider,
  ScrollProvider,
} from '@wix/form-fields';
import { type Form as FormEntity } from '@wix/ambassador-forms-v4-form/types';
import React, { type ReactNode, useMemo } from 'react';
import { useFormBi } from '../form/use-form-bi';
import type { WixRicosViewerProps } from '@wix/ricos';
import { type CountryCode } from '@wix/form-multiline-address';
import { SubmitStatusProvider } from '../../contexts/submit-status-context';
import { type PanoramaClientForComponent } from '@wix/fe-essentials/panorama-client';

export interface FormViewerProviderProps {
  i18n: i18n;
  httpClient?: IHttpClient;
  bi?: Logger;
  panorama?: PanoramaClientForComponent;
  biFormFillSessionId?: string;
  form: FormEntity;
  translationNamespace?: string;
  isMobile?: boolean;
  isEditor?: boolean;
  isComposer?: boolean;
  isRTL?: boolean;
  compId?: string;
  locale: string;
  regionalFormat: string;
  externalData?: ExternalData;
  forcedState?: ForcedState[];
  children: ReactNode;
  instance?: string;
  experiments?: Experiments;
  currency?: Currency;
  userData?: UserData;
  WixRicosViewer: React.ComponentType<WixRicosViewerProps>;
  addressTemplates?: AddressTemplate[];
  atlasBaseUrl?: string;
  fieldInitialData?: FieldInitialData;
  enableAnimations?: boolean;
}

export const FormViewerProvider = ({
  i18n,
  bi,
  biFormFillSessionId,
  form,
  isMobile,
  isEditor,
  isComposer,
  isRTL,
  compId,
  locale,
  regionalFormat,
  externalData,
  forcedState,
  instance,
  children,
  httpClient,
  panorama,
  experiments,
  translationNamespace,
  currency,
  userData,
  WixRicosViewer,
  addressTemplates,
  atlasBaseUrl,
  fieldInitialData,
  enableAnimations,
}: FormViewerProviderProps) => {
  const translations = useMemo(
    () => ({
      t: i18n.getFixedT(locale, translationNamespace),
      i18n,
    }),
    [locale, i18n, translationNamespace],
  );
  const formBi = useFormBi(form, bi, biFormFillSessionId);

  const config = useMemo(
    () => ({
      locale,
      regionalFormat,
      instance,
      isMobile,
      currency,
      atlasBaseUrl,
      userData,
      compId,
      isEditor,
      isComposer,
      isRTL,
      enableAnimations,
    }),
    [
      locale,
      regionalFormat,
      instance,
      isMobile,
      currency,
      atlasBaseUrl,
      userData,
      compId,
      isEditor,
      isComposer,
      isRTL,
      enableAnimations,
    ],
  );

  const forcedViewState = useMemo(() => forcedState ?? [], [forcedState]);

  return (
    <HttpClientContext.Provider value={{ httpClient }}>
      <PanoramaContext.Provider value={{ panorama }}>
        <TranslationContext.Provider value={translations}>
          <ScrollProvider>
            <FocusedFieldProvider>
              <FieldInitialDataContext.Provider value={fieldInitialData}>
                <BiContext.Provider value={formBi}>
                  <ExternalDataProvider externalData={externalData}>
                    <ConfigContext.Provider value={config}>
                      <ForcedStateContext.Provider value={forcedViewState}>
                        <FormContext.Provider value={form}>
                          <AddressFormsContext.Provider
                            value={{
                              getCountryAddressForm: (country?: CountryCode) =>
                                getAddressForm(addressTemplates, country),
                              addressTemplates,
                            }}
                          >
                            <ExperimentsContext.Provider value={experiments}>
                              <EventsProvider>
                                <WixRicosViewerContext.Provider
                                  value={WixRicosViewer}
                                >
                                  <SubmitStatusProvider>
                                    {children}
                                  </SubmitStatusProvider>
                                </WixRicosViewerContext.Provider>
                              </EventsProvider>
                            </ExperimentsContext.Provider>
                          </AddressFormsContext.Provider>
                        </FormContext.Provider>
                      </ForcedStateContext.Provider>
                    </ConfigContext.Provider>
                  </ExternalDataProvider>
                </BiContext.Provider>
              </FieldInitialDataContext.Provider>
            </FocusedFieldProvider>
          </ScrollProvider>
        </TranslationContext.Provider>
      </PanoramaContext.Provider>
    </HttpClientContext.Provider>
  );
};
